import React, { useCallback, useMemo, useState } from 'react';
import ReactGA from 'react-ga';
import { LogoIcon, TrustPilotBox } from 'src/components';
import { SText } from 'src/components/SText';
import { Screen } from 'src/components/Screen';
import { AdvisorCatalogLoader } from 'src/containers/advisorCatalogLoader';
import { DeliveryWidgetInfo } from 'src/features/DeliveryPublicColiInfo/DeliveryWidgetInfo';
import { TrackingColiPublicDataModel } from '../../tracking-colis-public.types';
import { BdcHistoryPublicItem } from './components/BdcHistoryPublicItem';
import { ColiWithHistoryPublic } from './components/ColiWithHistoryPublic/ColiWithHistoryPublic';
import { RefundHistoryPublicItem } from './components/RefundHistoryPublicItem';

type Props = {
  testID?: string;
  data: TrackingColiPublicDataModel;
};

export const TrackingColiPublicContent: React.FC<Props> = ({ data, testID = 'TrackingColiPublicContent' }) => {
  const [currentSelected, setCurrentSelected] = useState<string | null>(null);
  const { tracker, colis, bdcs, toAgency, currencySymbol, refunds } = data;

  const isBDC = useMemo(() => bdcs.some((bdc) => bdc.tracker === currentSelected), [bdcs, currentSelected]);

  const onSelectHandler = useCallback(
    (selected: string) => {
      if (currentSelected === selected) {
        return setCurrentSelected(null);
      } else {
        if (currentSelected) {
          ReactGA.event({
            category: 'Sender',
            action: isBDC ? 'consultBdcDetails' : 'consultColisDetails',
            label: `${tracker}_${currentSelected}`,
          });
        }
      }
      setCurrentSelected(selected);
    },
    [setCurrentSelected, currentSelected, isBDC, tracker],
  );

  const hasColisToTrack = useMemo(() => {
    return colis.some((colis) => colis.status !== 'CANCELLED');
  }, [colis]);

  return (
    <Screen className="m-auto">
      <div className="md:w-1/2 w-full mx-2 md:m-auto ">
        <LogoIcon className="w-40 md:w-64 my-2 m-auto" />

        {/* <AdvisorCatalogLoader zone="TrackingUnderColis" /> */}

        <SText
          label="public.tracking-number"
          classNames=" text-primary  font-semibold block  text-xl md:text-2xl text-center"
          translatingOptions={{ tracker }}
        />
        <SText
          label="public.colis-content-length"
          classNames="block text-primary my-2"
          translatingOptions={{ count: colis.length }}
        />
        <div>
          {colis.map((coli) => {
            return (
              <ColiWithHistoryPublic
                data={{ coli, toAgency }}
                key={coli.tracker}
                isOpen={true}
                onSelect={onSelectHandler}
              />
            );
          })}
        </div>
        <AdvisorCatalogLoader zone="TrackingUnderColis" />
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6226260594799382"
          crossOrigin="anonymous"
        ></script>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key="-if+f+o-ag+hi"
          data-ad-client="ca-pub-6226260594799382"
          data-ad-slot="2269656608"
        ></ins>
        <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>
        {!!bdcs.length && (
          <div>
            <SText
              label="public.bdcs-content-length"
              classNames="block text-primary my-2"
              translatingOptions={{ count: bdcs.length }}
            />
            {bdcs.map((bdc) => {
              return (
                <BdcHistoryPublicItem
                  bdc={bdc}
                  label="public.bdc-number"
                  colis={colis}
                  onSelect={onSelectHandler}
                  isOpen={bdc.tracker === currentSelected}
                  key={bdc.tracker}
                />
              );
            })}

            {refunds.map((refund) => {
              return (
                <RefundHistoryPublicItem
                  refund={refund}
                  label="public.refund-number"
                  onSelect={onSelectHandler}
                  isOpen={refund.id === currentSelected}
                  key={refund.id}
                />
              );
            })}
          </div>
        )}

        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6226260594799382"
          crossOrigin="anonymous"
        ></script>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key="-if+f+o-ag+hi"
          data-ad-client="ca-pub-6226260594799382"
          data-ad-slot="2269656608"
        ></ins>
        <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>

        {hasColisToTrack && (
          <DeliveryWidgetInfo currency={currencySymbol} toAgency={toAgency} restToPay={data.restToPay} />
        )}

        <AdvisorCatalogLoader zone="TrackingUnderColis" />
        <div className="my-4">
          <TrustPilotBox />
        </div>
      </div>
    </Screen>
  );
};
